import { zodResolver } from "@hookform/resolvers/zod";
import { memo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { ControlledCheckboxInput } from "@/components/FormInputs";
import { DateTimeInput } from "@/components/inputs/DateTimeInput";
import EditorInput from "@/components/inputs/EditorInput";
import { ImageUploadInput } from "@/components/inputs/ImageInput";
import { PlayerIdInput } from "@/components/inputs/PlayerIdInput";
import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

import { TournamentSeriesInput, zTournamentSeriesInput } from "./validation/tournament-series";

// Convert GraphQL data to form format
export const convertGraphQLToForm = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    standingsCalculation: any
): TournamentSeriesInput["standingsCalculation"] => {
    if (!standingsCalculation) {
        return { type: "manual" };
    }

    const method = standingsCalculation.method;
    if (!method) {
        return { type: "manual" };
    }

    if (method.t === "Points") {
        return {
            type: "points",
            tournamentCap: standingsCalculation.tournament_cap ?? null,
            playerRankCap: standingsCalculation.player_rank_cap ?? null,
        };
    } else if (method.t === "Rank" && method.c) {
        // Convert rank_points object back to comma-separated string
        const rankPoints = method.c.rank_points || {};
        const rankPointsArray: number[] = [];
        Object.keys(rankPoints)
            .sort((a, b) => parseInt(a) - parseInt(b))
            .forEach(key => {
                rankPointsArray[parseInt(key) - 1] = rankPoints[parseInt(key)];
            });
        const rankPointsString = rankPointsArray.join(",");

        return {
            type: "rank",
            tournamentCap: standingsCalculation.tournament_cap ?? null,
            playerRankCap: standingsCalculation.player_rank_cap ?? null,
            minPoints: method.c.min_points || 0,
            rankPointsString,
        };
    }

    return { type: "manual" };
};

// Convert form data to GraphQL format
export const convertFormToGraphQL = (data: TournamentSeriesInput) => {
    const {
        title,
        description,
        startAt,
        endAt,
        heroImageId,
        thumbnailImageId,
        previousWinnerPlayerId,
        isInternalOnly,
        isHidden,
        standingsInfo,
        standingsCalculation,
    } = data;

    let standingsCalculationValue = null;
    if (standingsCalculation && standingsCalculation.type !== "manual") {
        if (standingsCalculation.type === "points") {
            standingsCalculationValue = {
                player_rank_cap: standingsCalculation.playerRankCap,
                tournament_cap: standingsCalculation.tournamentCap,
                method: {
                    t: "Points",
                },
            };
        } else if (standingsCalculation.type === "rank") {
            // Parse comma-separated string into rank_points object
            const rankPointsMap: Record<number, number> = {};
            if (standingsCalculation.rankPointsString) {
                const points = standingsCalculation.rankPointsString.split(",").map(p => p.trim());
                points.forEach((point, index) => {
                    const pointValue = parseInt(point);
                    if (!isNaN(pointValue)) {
                        rankPointsMap[index + 1] = pointValue;
                    }
                });
            }

            standingsCalculationValue = {
                player_rank_cap: standingsCalculation.playerRankCap,
                tournament_cap: standingsCalculation.tournamentCap,
                method: {
                    t: "Rank",
                    c: {
                        min_points: standingsCalculation.minPoints,
                        rank_points: rankPointsMap,
                    },
                },
            };
        }
    }

    return {
        title,
        description,
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString(),
        heroImageId,
        thumbnailImageId,
        previousWinnerPlayerId,
        isInternalOnly,
        isHidden,
        standingsInfo,
        standingsCalculation: standingsCalculationValue,
    };
};

const TournamentSeriesFormComponent = ({
    onSubmit,
    defaultValues,
    submitText = "Create tournament series",
}: {
    onSubmit: SubmitHandler<TournamentSeriesInput>;
    defaultValues: TournamentSeriesInput;
    submitText?: string;
}) => {
    const form = useForm<z.infer<typeof zTournamentSeriesInput>>({
        defaultValues: {
            ...defaultValues,
            standingsCalculation: {
                type: "manual",
                tournamentCap: null,
                playerRankCap: null,
                minPoints: 0,
                rankPointsString: "",
                ...(defaultValues.standingsCalculation || {}),
            },
        },
        mode: "onChange",
        resolver: zodResolver(zTournamentSeriesInput),
    });

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Title</FormLabel>
                            <FormControl>
                                <Input placeholder="Title" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="startAt"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Start date</FormLabel>
                            <FormControl>
                                <DateTimeInput
                                    value={field.value}
                                    onValue={startAt => form.setValue("startAt", startAt)}
                                    className="w-full"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="endAt"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>End date</FormLabel>
                            <FormControl>
                                <DateTimeInput
                                    value={field.value}
                                    onValue={endAt => form.setValue("endAt", endAt)}
                                    className="w-full"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Description</FormLabel>
                            <FormControl>
                                <EditorInput
                                    initialValue={field.value}
                                    onChange={value => form.setValue("description", value)}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="previousWinnerPlayerId"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Last year's winner</FormLabel>
                            <FormControl>
                                <PlayerIdInput
                                    className="max-w-[500px] flex-1"
                                    playerId={field.value}
                                    onPlayerIdChange={field.onChange}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="standingsInfo"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Standings Info</FormLabel>
                            <FormControl>
                                <Input
                                    placeholder="Enter standings info"
                                    {...field}
                                    value={field.value ?? undefined}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="heroImageId"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Hero image</FormLabel>
                            <FormControl>
                                <ImageUploadInput
                                    className="w-full"
                                    imageId={field.value}
                                    onImageId={imageId => form.setValue("heroImageId", imageId)}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="thumbnailImageId"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Thumbnail image</FormLabel>
                            <FormControl>
                                <ImageUploadInput
                                    className="w-full"
                                    imageId={field.value}
                                    onImageId={imageId =>
                                        form.setValue("thumbnailImageId", imageId)
                                    }
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <div className="space-y-4 rounded-lg border p-6">
                    <h3 className="text-lg font-semibold">Standings Configuration</h3>

                    <FormField
                        control={form.control}
                        name="standingsCalculation.type"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Calculation Method</FormLabel>
                                <Select
                                    onValueChange={field.onChange}
                                    value={field.value || "manual"}
                                >
                                    <FormControl>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select calculation method" />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        <SelectItem value="manual">Manual</SelectItem>
                                        <SelectItem value="points">Points</SelectItem>
                                        <SelectItem value="rank">Rank</SelectItem>
                                    </SelectContent>
                                </Select>
                                <FormDescription>
                                    Manual: Manually set player standings. Points: Sum tournament
                                    points. Rank: Convert ranks to points.
                                </FormDescription>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {form.watch("standingsCalculation.type") !== "manual" && (
                        <div className="grid grid-cols-2 gap-4">
                            <FormField
                                control={form.control}
                                name="standingsCalculation.tournamentCap"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Tournament Cap</FormLabel>
                                        <FormControl>
                                            <Input
                                                type="number"
                                                placeholder="All tournaments"
                                                value={field.value ?? ""}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    field.onChange(val === "" ? null : val);
                                                }}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            Max tournaments to count (leave empty for all)
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="standingsCalculation.playerRankCap"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Player Rank Cap</FormLabel>
                                        <FormControl>
                                            <Input
                                                type="number"
                                                placeholder="All ranks"
                                                value={field.value ?? ""}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    field.onChange(val === "" ? null : val);
                                                }}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            Max player rank to get points (leave empty for all)
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    )}

                    {form.watch("standingsCalculation.type") === "rank" && (
                        <div className="space-y-4">
                            <FormField
                                control={form.control}
                                name="standingsCalculation.minPoints"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Minimum Points</FormLabel>
                                        <FormControl>
                                            <Input
                                                type="number"
                                                placeholder="0"
                                                {...field}
                                                value={field.value ?? 0}
                                                onChange={e =>
                                                    field.onChange(parseInt(e.target.value) || 0)
                                                }
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            Points awarded for ranks not listed below
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="standingsCalculation.rankPointsString"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Rank Points</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="25,20,18,15,12,10,8,6,4,2"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            Comma-separated points for each rank (1st, 2nd, 3rd,
                                            etc.)
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    )}
                </div>

                <FormField
                    control={form.control}
                    name="isInternalOnly"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Is internal only</FormLabel>
                            <FormControl>
                                <ControlledCheckboxInput
                                    checked={field.value}
                                    onValue={isInternalOnly =>
                                        form.setValue("isInternalOnly", isInternalOnly)
                                    }
                                    className="w-full"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="isHidden"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Hidden</FormLabel>
                            <FormControl>
                                <ControlledCheckboxInput
                                    checked={field.value}
                                    onValue={isHidden => form.setValue("isHidden", isHidden)}
                                    className="w-full"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <Button
                    type="submit"
                    disabled={!form.formState.isValid || form.formState.isSubmitting}
                >
                    {submitText}
                </Button>
            </form>
        </Form>
    );
};

export const TournamentSeriesForm = memo(TournamentSeriesFormComponent);
